import React, { useEffect, useState } from 'react';
import { Linking, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import {
  AUTO_TOPUP_BONUS_PERCENT,
  AUTO_TOPUP_MAX_THRESHOLD_CREDITS,
  AUTO_TOPUP_MIN_THRESHOLD_CREDITS,
  bonusCreditsFor,
  grantedCreditsFor,
  packClearsThreshold,
} from '@/lib/autoTopupOffer';

export const money = value => Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 0 });
export const topupStatus = {
  off: 'Off', ready: 'On', monthly_limit: 'Monthly limit reached', daily_limit: 'Daily refill limit reached', spacing: 'On',
  card_waiting: 'New payment method · waiting 24 hours', payment_pending: 'Payment being checked',
  fulfillment_pending: 'Payment received · credits being added', consent_required: 'Review required',
  pack_changed: 'Your credit pack changed', pack_unavailable: 'Choose a different credit pack',
  payment_method_changed: 'Review your payment method', provider_unavailable: 'Not available yet',
  no_plus_access: 'Active Plus required', account_hold: 'Billing account needs attention',
  payment_declined: 'Payment declined', above_threshold: 'On', balance_unavailable: 'Balance check pending',
  settings_changed: 'Settings changed · review required',
  usage_pending: 'Waiting for current usage to settle', period_changed: 'Waiting for the next balance check',
};

function volumeDiscount(pack, packs) {
  const base = packs.reduce((highest, item) => {
    if (!item?.credits || !(item.price > 0)) return highest;
    return Math.max(highest, item.price / item.credits);
  }, 0);
  if (!base || !pack?.credits || !(pack.price > 0)) return 0;
  const percent = Math.round((1 - (pack.price / pack.credits) / base) * 100);
  return percent >= 5 ? percent : 0;
}

function dollarsForCredits(credits, pack) {
  if (!pack?.credits || !(pack.price > 0) || !Number.isFinite(credits)) return null;
  return credits * (pack.price / pack.credits);
}

export function CheckRow({ checked, onChange, children, label, disabled }) {
  const { colors } = useTheme();
  return <Pressable accessibilityRole="checkbox" accessibilityLabel={label} accessibilityState={{ checked, disabled }} aria-checked={checked} disabled={disabled} onPress={() => onChange(!checked)} style={styles.checkRow}>
    <View style={[styles.checkbox, { borderColor: checked ? colors.text_color_primary : colors.border_color_secondary, backgroundColor: checked ? colors.text_color_primary : 'transparent' }]}>
      {checked && <Icon name="check" size={12} color={colors.solid_color_primary} />}
    </View><View style={{ flex: 1 }}>{children}</View>
  </Pressable>;
}

// Pure form: draft edits and checkbox clicks never write billing state. Only
// the explicit Save action sends the exact catalog price the customer reviewed.
// The 10% bonus is computed here for display and again on the server at grant time.
export default function AutoTopupEditor({ settings, packs, onSave, onCancel, onDisable, busy = false, error = '', cardLabel = 'Saved payment method', balanceCredits = null }) {
  const { colors, theme } = useTheme();
  const dark = isDarkTheme(theme);
  const [packId, setPackId] = useState(settings.creditPackId || packs[0]?.id);
  const [thresholdText, setThresholdText] = useState(String(settings.thresholdCredits ?? 250));
  const [limit, setLimit] = useState(String(settings.monthlySpendLimit ?? 50));
  const [unlimited, setUnlimited] = useState(settings.monthlySpendLimit === null);
  const [consent, setConsent] = useState(false);
  const pack = packs.find(p => p.id === String(packId));
  const granted = pack ? grantedCreditsFor(pack.credits) : 0;
  const bonus = pack ? bonusCreditsFor(pack.credits) : 0;
  const thresholdNumber = Number(thresholdText);
  useEffect(() => { setConsent(false); }, [pack?.price, pack?.credits, pack?.currency, settings.paymentMethodFingerprint]);
  const limitNumber = Number(limit);
  const validLimit = unlimited || (/^\d+(\.\d{1,2})?$/.test(limit) && limitNumber >= Number(pack?.price) && limitNumber <= 3000 && limitNumber >= 1);
  const validThreshold = Number.isSafeInteger(thresholdNumber)
    && thresholdNumber >= AUTO_TOPUP_MIN_THRESHOLD_CREDITS
    && thresholdNumber <= AUTO_TOPUP_MAX_THRESHOLD_CREDITS
    && !!pack
    && packClearsThreshold(granted, thresholdNumber);
  const validPack = !!pack && Number(pack.price) > 0 && (pack.currency || 'USD').toUpperCase() === 'USD' && validThreshold;
  const change = fn => value => { setConsent(false); fn(value); };
  const thresholdDollars = dollarsForCredits(thresholdNumber, pack);
  const save = () => {
    if (busy || !consent || !validPack || !validLimit) return;
    onSave({
      enabled: true, version: settings.version, provider: settings.provider || 'valmar',
      creditPackId: pack.id, thresholdCredits: thresholdNumber, monthlySpendLimit: unlimited ? null : limitNumber,
      consent: true, paymentMethodFingerprint: settings.paymentMethodFingerprint,
      quote: { creditPackId: pack.id, credits: pack.credits, priceCents: Math.round(pack.price * 100), currency: 'USD' },
    });
  };
  const rewardBg = dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.045)';
  const rewardBorder = dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
  const rewardText = dark ? '#f5f5f7' : '#1d1d1f';
  return <View style={styles.form}>
    <Text size="s" color="secondary">Automatically purchase credits when you’re running low.</Text>
    <View style={[styles.reward, { backgroundColor: rewardBg, borderColor: rewardBorder }]}>
      <Icon name="gift" size={16} color={rewardText} solid />
      <View style={{ flex: 1, gap: 2 }}>
        <Text size="s" weight="semibold" style={{ color: rewardText }}>{AUTO_TOPUP_BONUS_PERCENT}% more credits on every refill</Text>
        <Text size="s" color="secondary">Each automatic pack includes {AUTO_TOPUP_BONUS_PERCENT}% extra credits at the same price.</Text>
      </View>
    </View>
    <View style={styles.packs} accessibilityRole="radiogroup" accessibilityLabel="Automatic credit pack">
      {packs.map(item => {
        const selected = packId === item.id;
        const itemGranted = grantedCreditsFor(item.credits);
        const discount = volumeDiscount(item, packs);
        return <Pressable key={item.id} accessibilityRole="radio" accessibilityLabel={`${money(item.price)} for ${itemGranted.toLocaleString()} credits`} accessibilityState={{ checked: selected, disabled: busy }} aria-checked={selected} disabled={busy} onPress={() => change(setPackId)(item.id)} style={[styles.pack, { borderColor: selected ? colors.text_color_primary : colors.border_color_secondary, backgroundColor: selected ? colors.solid_color_secondary : 'transparent', borderWidth: selected ? 2 : 1 }]}>
          <Text size="l" weight="bold">{money(item.price)}</Text>
          <Text size="s" color="secondary">{itemGranted.toLocaleString()} credits</Text>
          <View style={styles.pills}>
            <View style={[styles.pill, { backgroundColor: rewardBg }]}><Text size="xxs" weight="semibold" style={{ color: rewardText }}>+{AUTO_TOPUP_BONUS_PERCENT}%</Text></View>
            {discount > 0 && <View style={[styles.pill, { backgroundColor: rewardBg }]}><Text size="xxs" weight="semibold" style={{ color: rewardText }}>{discount}% off</Text></View>}
          </View>
        </Pressable>;
      })}
    </View>
    {!validPack && <Text size="xs" color="secondary">Choose an available USD pack, and set a minimum balance one top-up can clear.</Text>}
    <View style={styles.field}>
      <Text size="s" weight="semibold">Minimum balance</Text>
      <View style={[styles.amountRow, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        <TextInput accessibilityLabel="Minimum balance in credits" keyboardType="number-pad" value={thresholdText} editable={!busy} onChangeText={change(setThresholdText)} style={[styles.input, { color: colors.text_color_primary }]} />
        <Text size="s" color="secondary">{thresholdDollars != null && Number.isFinite(thresholdDollars) ? `credits · ${money(thresholdDollars)}` : 'credits'}</Text>
      </View>
      {!validThreshold && <Text size="xs" color="secondary">Enter a whole number from {AUTO_TOPUP_MIN_THRESHOLD_CREDITS.toLocaleString()} to {AUTO_TOPUP_MAX_THRESHOLD_CREDITS.toLocaleString()} that one top-up can lift you past.</Text>}
    </View>
    <View style={styles.field}>
      <Text size="s" weight="semibold">Maximum monthly spend (optional)</Text>
      {!unlimited && <View style={[styles.amountRow, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        <Text size="m">$</Text>
        <TextInput accessibilityLabel="Monthly spending limit in dollars" keyboardType="decimal-pad" value={limit} editable={!busy} onChangeText={change(setLimit)} placeholder="No limit" placeholderTextColor={colors.text_color_tertiary} style={[styles.input, { color: colors.text_color_primary }]} />
      </View>}
      {unlimited && <View style={[styles.amountRow, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}><Text size="m" color="secondary">$  No limit</Text></View>}
      <CheckRow label="No monthly spending limit" checked={unlimited} onChange={change(setUnlimited)} disabled={busy}><Text size="s">No monthly limit</Text></CheckRow>
      {!validLimit && <Text size="xs" color="secondary">Enter a limit from {money(Math.max(1, pack?.price || 1))} to $3,000, or choose no monthly limit. Clearing this field does not remove your limit.</Text>}
      <Text size="xs" color="secondary">{money(settings.currentMonthSpend || 0)} spent or reserved this month. Resets on the first of each month (UTC).</Text>
    </View>
    <View style={[styles.summary, { backgroundColor: colors.solid_color_secondary }]}>
      <View style={styles.inline}><Icon name="credit-card" size={16} color={colors.text_color_secondary} /><Text size="s">{cardLabel}</Text></View>
      <Text size="xs" color="secondary">Refills start as soon as your balance drops below your minimum. With no monthly limit, refills are capped at 20 a day. New payment methods wait 24 hours. {pack ? `Each top-up adds ${bonus.toLocaleString()} bonus credits.` : ''}</Text>
    </View>
    <CheckRow label="Authorize automatic credit purchases" checked={consent} onChange={setConsent} disabled={busy || !validPack || !validLimit}>
      <Text size="s">I authorize {pack ? `${money(pack.price)} for ${granted.toLocaleString()} credits, including ${bonus.toLocaleString()} bonus credits,` : 'this credit purchase'} whenever my balance is below {validThreshold ? thresholdNumber.toLocaleString() : 'my minimum'}, {unlimited ? 'with no monthly spending limit and at most 20 purchases a day' : `up to ${validLimit ? money(limitNumber) : 'my limit'} per month`}.</Text>
    </CheckRow>
    <Text size="xs" color="secondary">You can turn this off at any time. A payment already in progress may still complete. <Text size="xs" onPress={() => Linking.openURL('https://gab.ai/terms-of-sale')} style={{ textDecorationLine: 'underline' }}>Terms of Sale</Text></Text>
    {!!error && <View accessibilityRole="alert"><Text size="s" color="danger">{error}</Text></View>}
    <View style={styles.footer}>
      <Text size="s" color="secondary">{balanceCredits == null ? '' : `Current balance: ${Number(balanceCredits).toLocaleString()} credits`}</Text>
      <View style={styles.actions}>
        {settings.enabled && !!onDisable && <Button text="Turn auto top-up off" onPress={onDisable} isDisabled={busy} />}
        <Button text="Back" onPress={onCancel} isDisabled={busy} />
        <Button text={busy ? 'Saving…' : settings.enabled ? 'Save' : 'Turn on auto top-up'} variant="primary" onPress={save} isDisabled={busy || !consent || !validPack || !validLimit} isLoading={busy} />
      </View>
    </View>
  </View>;
}

const styles = StyleSheet.create({
  form: { gap: 18 },
  reward: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, borderWidth: 1, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12 },
  packs: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  pack: { flexGrow: 1, flexBasis: 140, minWidth: 132, borderRadius: 14, paddingVertical: 16, paddingHorizontal: 12, alignItems: 'center', gap: 6 },
  pills: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 4 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 3 },
  field: { gap: 8 },
  amountRow: { borderWidth: 1, borderRadius: 12, paddingHorizontal: 14, minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 10 },
  input: { flex: 1, fontSize: 16, minHeight: 32, paddingVertical: 8 },
  checkRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start', paddingVertical: 3 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, marginTop: 2, alignItems: 'center', justifyContent: 'center' },
  summary: { padding: 14, borderRadius: 12, gap: 8 },
  inline: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  footer: { gap: 12 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
});
